import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import { galleryAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { 
  Plus, Image, Video, Trash2, Edit, Eye, Upload, X, Calendar, MapPin, Star, Grid
} from 'lucide-react';

const GalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const AlbumsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AlbumCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const AlbumCover = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.primary}40);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AlbumContent = styled.div`
  padding: 1.5rem;
`;

const AlbumTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AlbumMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1rem;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    flex-shrink: 0;
  }
`;

const AlbumStats = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-top: 1px solid ${props => props.theme.colors.border || '#f0f0f0'};
  font-size: ${props => props.theme.typography.fontSize.sm};
  
  span {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const AlbumActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  input[type="checkbox"] {
    width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
  }
`;

const InfoBanner = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15, ${props => props.theme.colors.primary}25);
  border: 1px solid ${props => props.theme.colors.primary}40;
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 1.5rem;
  display: flex;
  align-items: start;
  gap: 1rem;
  
  svg {
    flex-shrink: 0;
    color: ${props => props.theme.colors.primary};
  }
  
  div {
    flex: 1;
    
    h3 {
      margin: 0 0 0.5rem 0;
      font-size: ${props => props.theme.typography.fontSize.lg};
      font-weight: ${props => props.theme.typography.fontWeight.semibold};
      color: ${props => props.theme.colors.text.primary};
    }
    
    p {
      margin: 0;
      font-size: ${props => props.theme.typography.fontSize.sm};
      color: ${props => props.theme.colors.text.secondary};
      line-height: 1.6;
    }
  }
`;

const GalleryManagement = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    event_date: '',
    is_featured: false,
    status: 'published',
  });
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      setLoading(true);
      const response = await galleryAPI.getAlbums({});
      setAlbums(response.data || []);
    } catch (error) {
      console.error('Error fetching albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let albumId;
      
      if (editingAlbum) {
        await galleryAPI.updateAlbum(editingAlbum.id, formData);
        albumId = editingAlbum.id;
      } else {
        const response = await galleryAPI.createAlbum(formData);
        albumId = response.data.album?.id || response.data.id; // Handle both response structures
      }

      // Upload cover image if provided
      if (coverImageFile && albumId) {
        const coverFormData = new FormData();
        coverFormData.append('image', coverImageFile); // Backend expects 'image', not 'cover_image'
        await galleryAPI.uploadCoverImage(albumId, coverFormData);
      }

      fetchAlbums();
      resetForm();
      alert('Album saved successfully!');
    } catch (error) {
      console.error('Error saving album:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save album';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleEdit = (album) => {
    setEditingAlbum(album);
    setFormData({
      title: album.title || '',
      description: album.description || '',
      location: album.location || '',
      event_date: album.event_date || '',
      is_featured: album.is_featured || false,
      status: album.status || 'published',
    });
    // Show existing cover image
    if (album.cover_image) {
      setCoverImagePreview(getStorageUrl(album.cover_image));
    }
    setShowAlbumModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this album? All media will be deleted.')) {
      try {
        await galleryAPI.deleteAlbum(id);
        fetchAlbums();
      } catch (error) {
        console.error('Error deleting album:', error);
        alert('Failed to delete album');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      event_date: '',
      is_featured: false,
      status: 'published',
    });
    setCoverImageFile(null);
    setCoverImagePreview(null);
    setEditingAlbum(null);
    setShowAlbumModal(false);
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading albums...</div>;
  }

  return (
    <GalleryContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>Gallery Management</Title>
        <Button variant="primary" onClick={() => setShowAlbumModal(true)}>
          <Plus size={20} /> Create Album
        </Button>
      </Header>

      <InfoBanner theme={theme}>
        <Grid size={32} />
        <div>
          <h3>How to Add Photos & Videos to Albums</h3>
          <p>
            <strong>Step 1:</strong> Create an album using the "Create Album" button above.<br />
            <strong>Step 2:</strong> Click the blue "Add Photos & Videos" button on any album card.<br />
            <strong>Step 3:</strong> Upload images (single or multiple) or add videos from YouTube/Vimeo.<br />
            Easy as that! Your media will appear instantly in the album.
          </p>
        </div>
      </InfoBanner>

      <AlbumsGrid>
        {albums.map((album) => (
          <AlbumCard key={album.id} theme={theme}>
            <AlbumCover theme={theme}>
              {album.cover_image ? (
                <img src={getStorageUrl(album.cover_image)} alt={album.title} />
              ) : (
                <Image size={64} />
              )}
              {album.is_featured && (
                <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: theme.colors.warning, borderRadius: '50%', padding: '0.5rem' }}>
                  <Star size={20} fill={theme.colors.white} color={theme.colors.white} />
                </div>
              )}
            </AlbumCover>
            
            <AlbumContent theme={theme}>
              <AlbumTitle theme={theme}>{album.title}</AlbumTitle>
              
              <AlbumMeta theme={theme}>
                {album.event_date && (
                  <MetaRow>
                    <Calendar size={16} />
                    <span>{new Date(album.event_date).toLocaleDateString()}</span>
                  </MetaRow>
                )}
                {album.location && (
                  <MetaRow>
                    <MapPin size={16} />
                    <span>{album.location}</span>
                  </MetaRow>
                )}
              </AlbumMeta>
              
              <AlbumStats theme={theme}>
                <span><Image size={16} /> {album.images_count || 0} Photos</span>
                <span><Video size={16} /> {album.videos_count || 0} Videos</span>
              </AlbumStats>
              
              <AlbumActions>
                <Button 
                  variant="primary" 
                  size="small" 
                  onClick={() => navigate(`/app/gallery/${album.id}`)}
                  style={{ width: '100%', marginBottom: '0.5rem' }}
                >
                  <Upload size={16} /> Add Photos & Videos
                </Button>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Button 
                    variant="secondary" 
                    size="small" 
                    onClick={() => navigate(`/app/gallery/${album.id}`)}
                    style={{ flex: 1 }}
                  >
                    <Eye size={16} /> View Media ({album.images_count + album.videos_count || 0})
                  </Button>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button variant="outline" size="small" onClick={() => handleEdit(album)} style={{ flex: 1 }}>
                    <Edit size={16} /> Edit
                  </Button>
                  <Button variant="outline" size="small" onClick={() => handleDelete(album.id)} style={{ color: theme.colors.error, flex: 1 }}>
                    <Trash2 size={16} /> Delete
                  </Button>
                </div>
              </AlbumActions>
            </AlbumContent>
          </AlbumCard>
        ))}
      </AlbumsGrid>

      {showAlbumModal && (
        <Modal isOpen={showAlbumModal} onClose={resetForm} title={editingAlbum ? 'Edit Album' : 'Create Album'}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Album Title *</Label>
                <Input name="title" value={formData.title} onChange={handleInputChange} required />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Description</Label>
                <TextArea name="description" value={formData.description} onChange={handleInputChange} theme={theme} />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Event Date</Label>
                <Input type="date" name="event_date" value={formData.event_date} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Location</Label>
                <Input name="location" value={formData.location} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Cover Image</Label>
                <div style={{ 
                  border: '2px dashed #ddd',
                  borderRadius: '0.5rem',
                  padding: '1.5rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  background: coverImagePreview ? 'transparent' : '#f9f9f9'
                }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  {coverImagePreview ? (
                    <div>
                      <img 
                        src={coverImagePreview} 
                        alt="Cover preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px',
                          borderRadius: '0.5rem',
                          marginBottom: '0.5rem'
                        }} 
                      />
                      <p style={{ fontSize: '0.875rem', color: '#666', margin: 0 }}>
                        Click to change cover image
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Image size={48} style={{ color: theme.colors.primary, marginBottom: '0.5rem' }} />
                      <p style={{ margin: '0 0 0.25rem 0', color: theme.colors.text.primary, fontWeight: 500 }}>
                        Click to upload cover image
                      </p>
                      <p style={{ margin: 0, fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                        JPEG, PNG, GIF (Max 5MB)
                      </p>
                    </div>
                  )}
                </div>
              </FormGroup>

              <FormGroup theme={theme}>
                <Label theme={theme}>Status</Label>
                <select name="status" value={formData.status} onChange={handleInputChange} style={{ padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #ddd' }}>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </FormGroup>

              <CheckboxGroup theme={theme}>
                <input type="checkbox" name="is_featured" checked={formData.is_featured} onChange={handleInputChange} />
                <Label theme={theme}>Featured Album</Label>
              </CheckboxGroup>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" variant="primary">{editingAlbum ? 'Update' : 'Create'} Album</Button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </GalleryContainer>
  );
};

export default GalleryManagement;
